<template>
  <f7-panel
    right
    cover
    resizable
    :backdrop="false"
    class="contactDetailPanel detailPanel"
    @panel:opened="onPanelOpened"
    @panel:close="onPanelClose"
  >
    <f7-view>
      <f7-page page-with-subnavbar class="detailPage">
        <f7-navbar no-shadow>
          <f7-nav-left>
            <f7-link panel-close class="detailIconBtn" :aria-label="$t('common.close')">
              <f7-icon ios="f7:multiply" aurora="f7:multiply" md="material:close"></f7-icon>
            </f7-link>
          </f7-nav-left>
          <f7-nav-title class="detailNavTitle">{{ detailName }}</f7-nav-title>
        </f7-navbar>

        <f7-list dividers-ios strong-ios class="detailList">
          <f7-list-group>
            <div class="detailHero">
              <div class="detailAvatar">
                <img
                  v-if="avatarUrl"
                  class="detailAvatar__media"
                  :src="avatarUrl"
                  :alt="detailName"
                />
                <f7-badge v-else color="auto" class="detailAvatar__badge">
                  <template v-if="initials">{{ initials }}</template>
                  <f7-icon
                    v-else
                    ios="f7:person_fill"
                    aurora="f7:person_fill"
                    md="material:person"
                  ></f7-icon>
                </f7-badge>
              </div>

              <div class="detailHero__body">
                <div v-if="detailHeadline" class="detailHero__headline">
                  {{ detailHeadline }}
                </div>
                <div v-if="presenceLabel" class="detailStatus" :class="isOnline ? 'is-online' : 'is-offline'">
                  <span class="detailStatus__dot"></span>
                  <span>{{ presenceLabel }}</span>
                </div>
              </div>

              <div v-if="canEditAvatar && !isSavingAvatar" class="detailAvatar__actions">
                <template v-if="avatarDraft">
                  <f7-link
                    raised
                    class="detailIconBtn is-primary"
                    icon-f7="checkmark_alt"
                    :aria-label="$t('common.save')"
                    @click="commitAvatar"
                  ></f7-link>
                  <f7-link
                    raised
                    class="detailIconBtn is-danger"
                    icon-f7="xmark_circle_fill"
                    :aria-label="$t('tamtammessage.removePhoto')"
                    @click="discardAvatar"
                  ></f7-link>
                </template>
                <f7-link
                  v-else
                  raised
                  class="detailIconBtn"
                  icon-f7="camera_fill"
                  :aria-label="$t('tamtammessage.changePhoto')"
                  @click="pickAvatar"
                ></f7-link>
              </div>
            </div>
          </f7-list-group>

          <f7-list-group v-if="isGroupChat">
            <li class="item-content item-input detailField">
              <div class="item-inner">
                <label class="detailField__label" for="detailNameInput">{{ $t("group.groupName") }}</label>
                <div class="item-input-wrap">
                  <input
                    id="detailNameInput"
                    ref="nameInput"
                    type="text"
                    class="detailField__input"
                    :value="nameDraft"
                    :readonly="editingField !== 'name'"
                    :placeholder="$t('group.groupName')"
                    @input="nameDraft = $event.target.value"
                    @keyup.enter="commitField('name')"
                    @keyup.esc="cancelFieldEdit"
                    @blur="onFieldBlur('name')"
                  />
                  <f7-link
                    v-if="canEditInfo"
                    class="detailField__action"
                    :icon-f7="editingField === 'name' ? 'checkmark_alt' : 'pencil'"
                    :aria-label="editingField === 'name' ? $t('common.save') : $t('common.edit')"
                    @click="editingField === 'name' ? commitField('name') : startFieldEdit('name')"
                  ></f7-link>
                </div>
              </div>
            </li>

            <li class="item-content item-input detailField">
              <div class="item-inner">
                <label class="detailField__label" for="detailAboutInput">{{ $t("tamtammessage.about") }}</label>
                <div class="item-input-wrap">
                  <input
                    id="detailAboutInput"
                    ref="aboutInput"
                    type="text"
                    class="detailField__input"
                    :value="aboutDraft"
                    :readonly="editingField !== 'about'"
                    :placeholder="$t('groups.noDescription')"
                    @input="aboutDraft = $event.target.value"
                    @keyup.enter="commitField('about')"
                    @keyup.esc="cancelFieldEdit"
                    @blur="onFieldBlur('about')"
                  />
                  <f7-link
                    v-if="canEditInfo"
                    class="detailField__action"
                    :icon-f7="editingField === 'about' ? 'checkmark_alt' : 'pencil'"
                    :aria-label="editingField === 'about' ? $t('common.save') : $t('common.edit')"
                    @click="editingField === 'about' ? commitField('about') : startFieldEdit('about')"
                  ></f7-link>
                </div>
              </div>
            </li>

            <f7-block-footer v-if="creatorName || createdLabel">
              <span v-if="creatorName">{{ $t("tamtammessage.createdBy") }} {{ creatorName }}</span>
              <span v-if="creatorName && createdLabel" class="detailFooterDivider"></span>
              <span v-if="createdLabel">{{ createdLabel }}</span>
            </f7-block-footer>
          </f7-list-group>

          <f7-list-group v-if="isGroupChat && participants.length > 0">
            <f7-block-title small>
              {{ $t("tamtammessage.participants") }} · {{ participants.length }}
            </f7-block-title>

            <f7-list-item
              v-if="canManageParticipants"
              link="#"
              :title="$t('tamtammessage.addNewParticipant')"
              @click="addParticipantGroupRightPanel()"
            >
              <template #media>
                <f7-badge color="auto" class="detailRowBadge">
                  <f7-icon
                    ios="f7:person_crop_circle_fill_badge_plus"
                    aurora="f7:person_crop_circle_fill_badge_plus"
                    md="material:person_add"
                  ></f7-icon>
                </f7-badge>
              </template>
            </f7-list-item>

            <f7-searchbar
              v-if="participants.length > participantPreviewLimit"
              class="no-shadow searchbar-inline margin-horizontal"
              search-container=".chatParticipantsList"
              search-in=".item-title, .item-subtitle, .item-footer, .item-text"
              :disable-button="!theme.aurora"
              :placeholder="$t('tamtammessage.searchParticipant')"
              :backdrop="false"
              @search="isParticipantSearchActive = true"
              @search:clear="isParticipantSearchActive = false"
            />
            <f7-list dividers-ios strong-ios class="searchbar-not-found">
              <f7-list-item :title="$t('common.nothingFound')"></f7-list-item>
            </f7-list>
          </f7-list-group>

          <f7-list-group
            class="chatParticipantsList searchbar-found"
            v-if="isGroupChat && participants.length > 0"
          >
            <f7-list-item
              accordion-item
              v-for="participant in visibleParticipants"
              :key="participant.userId || participant.id"
              :title="participant.name || participant.username"
              :text="participant.tag"
            >
              <template #media>
                <f7-badge color="auto" class="detailRowBadge is-small">
                  <img
                    v-if="participant.avatar"
                    class="detailRowBadge__media"
                    :src="participant.avatar"
                    :alt="participant.name"
                  />
                  <template v-else>{{ getInitial(participant.name) }}</template>
                </f7-badge>
              </template>
              <template #after>
                <f7-badge v-if="isParticipantAdmin(participant)" color="auto" class="detailAdminBadge">
                  {{ $t("common.admin") }}
                </f7-badge>
              </template>
              <template #footer>
                <span
                  class="detailStatus is-small"
                  :class="participant.online ? 'is-online' : 'is-offline'"
                >
                  <span class="detailStatus__dot"></span>
                  <span>{{ participant.online ? $t("tamtammessage.online") : $t("tamtammessage.offline") }}</span>
                </span>
              </template>
              <f7-accordion-content v-if="canManageParticipantRow(participant)">
                <div class="detailActions">
                  <f7-button
                    small
                    fill
                    color="red"
                    class="detailActions__btn"
                    :text="$t('tamtammessage.removeAsParticipant')"
                    @click="removeParticipant(participant)"
                  ></f7-button>
                  <f7-button
                    v-if="!isParticipantAdmin(participant)"
                    small
                    fill
                    color="green"
                    class="detailActions__btn"
                    :text="$t('tamtammessage.grantAdminRight')"
                    @click="grantAdminRole(participant)"
                  ></f7-button>
                  <f7-button
                    v-else
                    small
                    fill
                    color="orange"
                    class="detailActions__btn"
                    :text="$t('tamtammessage.withdrawAdminRight')"
                    @click="withdrawAdminRole(participant)"
                  ></f7-button>
                </div>
              </f7-accordion-content>
            </f7-list-item>

            <f7-list-item
              v-if="hiddenParticipantCount > 0"
              link="#"
              divider
              :title="`+${hiddenParticipantCount}`"
              @click="isParticipantListExpanded = true"
            ></f7-list-item>
          </f7-list-group>

          <f7-list-group v-if="isGroupChat">
            <f7-list-item
              link="#"
              :title="$t('tamtammessage.exitGroup')"
              class="detailActionRow is-danger"
              @click="leaveGroup(group)"
            >
              <template #media>
                <f7-icon
                  ios="f7:square_arrow_left"
                  aurora="f7:square_arrow_left"
                  md="f7:square_arrow_left"
                  color="red"
                ></f7-icon>
              </template>
            </f7-list-item>
            <f7-list-item
              :title="$t('tamtammessage.reportGroup')"
              class="detailActionRow is-disabled"
            >
              <template #media>
                <f7-icon
                  ios="f7:hand_thumbsdown"
                  aurora="f7:hand_thumbsdown"
                  md="material:thumb_down"
                  color="red"
                ></f7-icon>
              </template>
            </f7-list-item>
          </f7-list-group>

          <f7-list-group v-if="!isGroupChat && counterPartUser && counterPartUser.id">
            <f7-list-item
              link="#"
              :title="$t('tamtammessage.blockUser')"
              class="detailActionRow is-danger"
              @click="blockUser(counterPartUser)"
            >
              <template #media>
                <f7-icon ios="f7:xmark_circle_fill" aurora="f7:xmark_circle_fill" md="material:cancel" color="red"></f7-icon>
              </template>
            </f7-list-item>
            <f7-list-item
              link="#"
              :title="$t('tamtammessage.deleteChat')"
              class="detailActionRow is-danger"
              @click="deleteChat(group)"
            >
              <template #media>
                <f7-icon ios="f7:trash_fill" aurora="f7:trash_fill" md="material:delete" color="red"></f7-icon>
              </template>
            </f7-list-item>
            <f7-list-item :title="$t('tamtammessage.reportUser')" class="detailActionRow is-disabled">
              <template #media>
                <f7-icon
                  ios="f7:hand_thumbsdown"
                  aurora="f7:hand_thumbsdown"
                  md="material:thumb_down"
                  color="red"
                ></f7-icon>
              </template>
            </f7-list-item>
          </f7-list-group>

          <f7-list-group v-if="!isGroupChat && groupsInCommon.length > 0">
            <f7-block-title small>
              {{ $t("tamtammessage.groupInCommon") }} · {{ groupsInCommon.length }}
            </f7-block-title>
            <f7-list-item
              v-for="comGroup in groupsInCommon"
              :key="comGroup.id"
              :link="'/message/' + comGroup.id + '/' + (comGroup.groupType || 'GROUP')"
              :title="comGroup.name"
            >
              <template #media>
                <f7-badge color="auto" class="detailRowBadge is-small">
                  <template v-if="comGroup.avatar">
                    <img class="detailRowBadge__media" :src="comGroup.avatar" :alt="comGroup.name" />
                  </template>
                  <template v-else>{{ getInitial(comGroup.name) }}</template>
                </f7-badge>
              </template>
            </f7-list-item>
          </f7-list-group>
        </f7-list>
      </f7-page>
    </f7-view>
  </f7-panel>
</template>

<script setup>
import { f7, theme } from "framework7-vue";
import { ref, computed, watch, nextTick, getCurrentInstance } from "vue";
import { storeToRefs } from "pinia";
import i18n from "@/js/i18n.js";
import { useMessageStore } from "../../js/pinia-stores/message.js";
import { useAuthStore } from "../../js/pinia-stores/auth.js";
import { useTabletScreen } from "../../js/composables/useMediaQuery.js";
import { useMessage } from "../../js/composables/useMessage.js";
import { useAttachDocs } from "../../js/composables/useAttachDocs.js";
import { formatDate, useTimeAgo } from "../../js/utils/datetime.js";

const props = defineProps({
  f7route: Object,
  f7router: Object,
  groupType: String,
  chatGroupAdminList: Array,
  isGroupAdmin: Boolean,
  commonGroups: Array,
  chatGroupParticipantList: Array,
});

const participantPreviewLimit = 20;

const messageStore = useMessageStore();
const authStore = useAuthStore();
const isTablet = useTabletScreen().matches;
const vm = getCurrentInstance()?.proxy;

const { profile, counterPartUser, group } = storeToRefs(messageStore);
const { documentsUrl } = storeToRefs(authStore);

const {
  blockUser,
  deleteChat,
  leaveGroup,
  removeParticipant,
  grantAdminRole,
  withdrawAdminRole,
  addParticipantGroupRightPanel,
  reducePageWidth,
  resetPageWidth,
} = useMessage({
  f7route: props.f7route,
  f7router: props.f7router,
  isTablet,
  profile,
  group,
});

const { pickImages } = useAttachDocs({
  f7route: props.f7route,
  f7router: props.f7router,
  isTablet,
});

const nameInput = ref(null);
const aboutInput = ref(null);
const nameDraft = ref("");
const aboutDraft = ref("");
const editingField = ref(null);
const avatarDraft = ref(null);
const isSavingAvatar = ref(false);
const isParticipantSearchActive = ref(false);
const isParticipantListExpanded = ref(false);
let pendingPickIds = null;

function getInitial(name) {
  return vm?.$filtersGetInitial?.getInitial(name) || "";
}

const conversationType = computed(() => props.groupType || group.value?.groupType || "");
const isPersonal = computed(() => conversationType.value === "PERSONAL");
const isGroupChat = computed(
  () => conversationType.value === "GROUP" || conversationType.value === "SUBGROUP"
);
const isAssociatedChat = computed(() => Boolean(group.value?.associatedId));

const detailName = computed(() => {
  if (isPersonal.value) {
    return (
      counterPartUser.value?.name ||
      counterPartUser.value?.username ||
      counterPartUser.value?.tag ||
      counterPartUser.value?.phoneNumber ||
      ""
    );
  }
  return group.value?.name || "";
});

const initials = computed(() => getInitial(detailName.value));
const avatarUrl = computed(
  () => avatarDraft.value?.url || group.value?.avatar || counterPartUser.value?.avatar || ""
);

const lastSeenAt = computed(() => counterPartUser.value?.timeOffline);
const lastSeenLabel = useTimeAgo(lastSeenAt);

const participants = computed(() => {
  if (Array.isArray(props.chatGroupParticipantList) && props.chatGroupParticipantList.length > 0) {
    return props.chatGroupParticipantList;
  }
  return Array.isArray(group.value?.groupParticipantList) ? group.value.groupParticipantList : [];
});

const onlineParticipantCount = computed(
  () => participants.value.filter((participant) => participant.online).length
);

const isOnline = computed(() =>
  isPersonal.value ? counterPartUser.value?.online === true : onlineParticipantCount.value > 0
);

const presenceLabel = computed(() => {
  if (isPersonal.value) {
    if (counterPartUser.value?.online) {
      return i18n.global.t("tamtammessage.online");
    }
    return lastSeenLabel.value
      ? i18n.global.t("tamtammessage.lastSeen", { time: lastSeenLabel.value })
      : i18n.global.t("tamtammessage.offline");
  }
  return onlineParticipantCount.value > 0
    ? `${onlineParticipantCount.value} ${i18n.global.t("tamtammessage.online")}`
    : "";
});

const detailHeadline = computed(() => {
  if (isGroupChat.value) {
    return participants.value.length > 0
      ? i18n.global.t("groups.members", { count: participants.value.length })
      : "";
  }
  return counterPartUser.value?.phoneNumber || counterPartUser.value?.tag || "";
});

const creatorName = computed(() => {
  const creatorId = group.value?.createdBy;
  if (!creatorId) return "";
  return participants.value.find((participant) => participant.userId === creatorId)?.name || "";
});

const createdLabel = computed(() =>
  group.value?.created ? formatDate(group.value.created, "LL") : ""
);

const groupsInCommon = computed(() =>
  (Array.isArray(props.commonGroups) ? props.commonGroups : []).filter(
    (item) => item && item.groupType !== "PERSONAL"
  )
);

const canEditInfo = computed(() => isGroupChat.value && props.isGroupAdmin === true);
const canEditAvatar = computed(() => canEditInfo.value);
const canManageParticipants = computed(
  () =>
    canEditInfo.value &&
    (!isAssociatedChat.value || conversationType.value === "SUBGROUP")
);

const visibleParticipants = computed(() =>
  isParticipantSearchActive.value || isParticipantListExpanded.value
    ? participants.value
    : participants.value.slice(0, participantPreviewLimit)
);

const hiddenParticipantCount = computed(() => {
  if (isParticipantSearchActive.value || isParticipantListExpanded.value) return 0;
  return Math.max(0, participants.value.length - participantPreviewLimit);
});

function isParticipantAdmin(participant) {
  return (props.chatGroupAdminList || []).some(
    (admin) => admin.userId && admin.userId === participant.userId
  );
}

function canManageParticipantRow(participant) {
  return canManageParticipants.value && participant.userId !== profile.value?.sub;
}

function resetDrafts() {
  nameDraft.value = group.value?.name || "";
  aboutDraft.value = group.value?.description || group.value?.about || "";
  editingField.value = null;
}

function discardAvatar() {
  const draft = avatarDraft.value;
  avatarDraft.value = null;
  pendingPickIds = null;
  if (draft?.fileId) authStore.unSetDocumentsUrl({ fileId: draft.fileId });
}

function onPanelOpened() {
  resetPanelState();
  reducePageWidth();
}

function onPanelClose() {
  discardAvatar();
  resetPageWidth();
}

function resetPanelState() {
  discardAvatar();
  resetDrafts();
  isParticipantSearchActive.value = false;
  isParticipantListExpanded.value = false;
}

async function pickAvatar() {
  if (!canEditAvatar.value || isSavingAvatar.value) return;
  pendingPickIds = new Set(documentsUrl.value.map((doc) => doc.fileId));
  await pickImages();
}

async function commitAvatar() {
  const draft = avatarDraft.value;
  if (!draft?.file || isSavingAvatar.value) return;
  isSavingAvatar.value = true;
  messageStore.error = null;
  try {
    const forms = new FormData();
    forms.append("file", draft.file, "avatar");
    forms.append("groupId", group.value?.id);
    await messageStore.updateGroupAvatar({ forms });
    if (messageStore.error) throw new Error(messageStore.error);
    discardAvatar();
  } catch (err) {
    f7.dialog.alert({
      title: i18n.global.t("common.updateImageFailed"),
      text: err?.message || i18n.global.t("common.updateFailed"),
    });
  } finally {
    isSavingAvatar.value = false;
  }
}

async function startFieldEdit(field) {
  if (!canEditInfo.value || editingField.value === field) return;
  if (editingField.value) await commitField(editingField.value);
  editingField.value = field;
  await nextTick();
  (field === "name" ? nameInput.value : aboutInput.value)?.focus?.();
}

async function commitField(field) {
  if (editingField.value !== field) return;
  const change = field === "name" ? nameDraft.value.trim() : aboutDraft.value.trim();
  const current = field === "name" ? group.value?.name : group.value?.description || group.value?.about;
  editingField.value = null;
  (field === "name" ? nameInput.value : aboutInput.value)?.blur?.();
  if (change === (current || "")) return;
  await messageStore.updateGroupInfo({
    changeType: field === "name" ? "groupName" : "description",
    change,
    groupId: group.value?.id,
  });
}

function cancelFieldEdit() {
  resetDrafts();
}

function onFieldBlur(field) {
  if (editingField.value === field) commitField(field);
}

watch(
  documentsUrl,
  (docs) => {
    if (!pendingPickIds || !Array.isArray(docs)) return;
    const picked = docs.find(
      (doc) => doc.fileType === "image" && !pendingPickIds.has(doc.fileId)
    );
    if (!picked) return;
    discardAvatar();
    avatarDraft.value = picked;
    pendingPickIds = null;
  },
  { deep: true }
);

watch(
  () => group.value?.id,
  () => {
    resetPanelState();
  }
);

resetPanelState();
</script>

<style scoped>
.detailPanel {
  min-width: 100%;
  border-left: var(--f7-page-master-border-width) solid var(--f7-page-master-border-color);
}

@media (min-width: 768px) {
  .detailPanel {
    min-width: 418px;
  }
}

.detailPage {
  background: var(--avocash-surface, #f4f7f2);
}

.detailList {
  margin-top: 0;
  background: transparent;
}

.detailNavTitle {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detailHero {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 16px;
}

.detailAvatar {
  position: relative;
  flex: 0 0 auto;
}

.detailAvatar__media,
.detailAvatar__badge {
  width: 96px;
  height: 96px;
  border-radius: 24px;
  object-fit: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 600;
  margin: 0;
}

.detailAvatar__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: auto;
}

.detailHero__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detailHero__headline {
  font-size: 15px;
  font-weight: 600;
  color: var(--f7-list-item-title-text-color, #1c1c1e);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detailStatus {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--f7-on-surface-variant, rgba(28, 28, 30, 0.5));
}

.detailStatus.is-small {
  font-size: 12px;
}

.detailStatus__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  flex: 0 0 auto;
}

.detailStatus.is-online {
  color: var(--f7-theme-color, #1a6b42);
}

.detailStatus.is-offline {
  color: rgba(28, 28, 30, 0.45);
}

.detailField {
  padding-top: 4px;
}

.detailField__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgba(28, 28, 30, 0.45);
  margin-bottom: 2px;
}

.detailField__input {
  font-size: var(--f7-block-title-medium-font-size);
  font-weight: var(--f7-block-title-medium-font-weight);
  color: var(--f7-block-title-medium-text-color);
  background: transparent;
}

.detailField__input[readonly] {
  opacity: 1;
  -webkit-text-fill-color: currentColor;
}

.detailField__action {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  color: var(--f7-theme-color, #1a6b42);
}

.detailFooterDivider {
  display: inline-block;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
  vertical-align: middle;
  margin: 0 6px;
  opacity: 0.5;
}

.detailRowBadge {
  width: 40px;
  height: 40px;
  border-radius: 20px;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  overflow: hidden;
}

.detailRowBadge.is-small {
  width: 34px;
  height: 34px;
  border-radius: 17px;
  font-size: 13px;
}

.detailRowBadge__media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detailAdminBadge {
  font-size: 11px;
  font-weight: 600;
  margin: 0;
}

.detailActions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
}

.detailActions__btn {
  flex: 1 1 140px;
  min-width: 120px;
  font-size: 13px;
  --f7-button-border-radius: 999px;
}

.detailActionRow.is-danger {
  --f7-list-item-title-text-color: var(--f7-theme-color-red, #d0342c);
}

.detailActionRow.is-disabled {
  opacity: 0.55;
}

.detailIconBtn {
  width: 32px;
  height: 32px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(28, 28, 30, 0.06);
  color: var(--f7-list-item-title-text-color, #1c1c1e);
  --f7-link-icon-color: currentColor;
}

.detailIconBtn.is-primary {
  background: var(--f7-theme-color, #1a6b42);
  color: #ffffff;
}

.detailIconBtn.is-danger {
  background: rgba(208, 52, 44, 0.12);
  color: var(--f7-theme-color-red, #d0342c);
}
</style>
